import { useEffect, type RefObject } from "react";
import { isEditableTarget, scrollToElement } from "@/utils/campaign";
import { KEYBOARD_SHORTCUTS } from "@/utils/keyboard";

/**
 * Custom hook for handling search keyboard shortcuts
 *
 * Listens for specified key presses to focus the search input and scroll it into view
 *
 * @param inputRef - Reference to the input element
 * @param containerRef - Reference to the container element
 * @param enabled - Whether the keyboard shortcut is enabled
 * @param shortcuts - Array of keyboard keys to listen for (defaults to KEYBOARD_SHORTCUTS.SEARCH_DEFAULT)
 */
export const useSearchKeyboardShortcut = (
  inputRef: RefObject<HTMLInputElement | null>,
  containerRef: RefObject<HTMLDivElement | null>,
  enabled: boolean,
  shortcuts: Array<string> = [...KEYBOARD_SHORTCUTS.SEARCH_DEFAULT],
) => {
  useEffect(() => {
    // Only register keyboard shortcut when enabled
    if (!enabled) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle specified keys without modifier keys
      if (!shortcuts.includes(event.key) || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      const target = event.target as HTMLElement | null;
      // Don't intercept if user is already in an input field
      if (isEditableTarget(target)) {
        return;
      }

      event.preventDefault();

      // Scroll container into view
      const element = containerRef.current;
      if (element) {
        scrollToElement(element);
      }

      // Focus input
      inputRef.current?.focus({ preventScroll: true });
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [enabled, inputRef, containerRef, shortcuts]);
};
